import { createPicoX } from "../../libraries/picoxweb/picox";

import { updateLandingPageAndPersistentUTMParams } from "./updateLandingPageAndPicoUTMParams";

// Singleton instance management
let picoXInstance = null;
let initializationPromise = null;
let username = undefined;

// Configuration for PicoX
const PICOX_CONFIG = {
  // This is the identification token for the `mileiq_marketing_web` source.
  // It is not a sensitive data, so we can keep it in the code.
  identificationToken: "c233adf3-d100-46d8-a0fc-826bf6bf6daa",
  additionalIdentifiers: () => ({
    username: username,
  }),
  isDevelopment: window.location.hostname != 'mileiq.com',
  cookieDomain: window.location.hostname == 'mileiq.com' ? '.mileiq.com' : '.mileiq.dev',
};

/**
 * Initialize PicoX with proper error handling and singleton pattern.
 * This function ensures that the PicoX instance is initialized only once,
 * while the configuration can be updated multiple times (for example to update the username,
 * which is available after the signup/login of the user).
 * @param {string} usernameParam - Optional username to pass as additional identifier, if present.
 * @returns {Promise<PicoX>} PicoX instance
 */
export async function initializeOrUpdatePicoXIfNeeded(usernameParam) {
  if (usernameParam) {
    username = usernameParam;
  }
  // Return existing instance if already initialized
  if (picoXInstance) {
    return picoXInstance;
  }

  // Return existing promise if initialization is in progress
  if (initializationPromise) {
    return initializationPromise;
  }

  // Start initialization
  initializationPromise = createPicoX({ ...PICOX_CONFIG })
    .then((instance) => {
      picoXInstance = instance;
      console.warn("[PicoX] Successfully initialized");
      return instance;
    })
    .catch((error) => {
      initializationPromise = null; // Allow retry on next call
      console.error("[PicoX] Initialization failed:", error);
      throw error;
    });

  initializationPromise.then(async (instance) => {
    // Track attribution event, needed for PUA.
    const [sessionUTMParams, persistentUTMParams] =
      await updateLandingPageAndPersistentUTMParams();
    instance.track("utm_params_tracked", {
      persistent: persistentUTMParams,
      landing_page_info: sessionUTMParams,
    });
    console.warn("[PicoX] Successfully tracked utm params");
  });

  return initializationPromise;
}

/**
 * Get the PicoX instance, initializing if necessary
 * @returns {Promise<PicoX>} PicoX instance
 */
export async function getPicoX() {
  return initializeOrUpdatePicoXIfNeeded();
}

/**
 * Track an event with PicoX, handling initialization automatically
 * @param {string} eventName - Name of the event to track
 * @param {Object} eventData - Event data payload
 * @returns {Promise<void>}
 */
export const trackPicoXEvent = (eventName, eventData) => {
  try {
    getPicoX().then((picoX) => {
      picoX.track(eventName, eventData);
    });
  } catch (error) {
    report(error);
    console.error("[PicoX] Failed to track event:", eventName, error);
    // Don't throw - analytics failures shouldn't break the app
  }
};
